/**
 * Shared auth types — imported by both frontend (aws-amplify) and backend (aws-jwt-verify).
 * Single source of truth: if the backend AuthContext grows a field, add it here and
 * TypeScript will surface the gap in the frontend immediately.
 */

// US-049 — resource-scoped vocabulary (generated; reclassification §3.3). Added
// ADDITIVELY alongside the legacy keys below (Phase 0, ships dark).
import type { ClientSignal } from "./client";
import { RESOURCE_SCOPED_KEYS } from "./permissionCatalog.generated";
import type { LegalGateState } from "./legal";
export {
  RESOURCES,
  ACTION_KEYS,
  RESOURCE_SCOPED_KEYS,
  keyFor,
  isActionKey,
} from "./permissionCatalog.generated";
export type { Resource, Verb, ActionKey } from "./permissionCatalog.generated";

/**
 * Every configurable permission key — now EXACTLY the resource-scoped catalog
 * (US-049). US-077 removed all 37 legacy audience-coupled keys: 33 had 1:1
 * resource-scoped successors; the final 4 (residents:delete, schedules:view,
 * reports:phi, users:assign_platform_roles) were retired by converting their last
 * call sites (users:assign_platform_roles → the isPlatformAdmin flag in
 * admin/users/[id]/roles) and dropping their unused fixtures. The `oldToNew` map
 * + migration tooling keep the legacy strings for the per-env DB migration only.
 */
export const PERMISSION_KEYS = [...RESOURCE_SCOPED_KEYS] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/**
 * Stable role keys — used by code to identify built-in roles.
 * Display name (roles.name in DB) is editable by admins; role_key never changes.
 * Custom roles created by admins have role_key = null and grant permissions only.
 */
export type AppRole =
  | "role:platform:admin"
  | "role:platform:manager"
  | "role:org:admin"
  | "role:facility:manager"
  | "role:staff:medical"
  | "role:staff:care"
  | "role:audit:admin"
  | "role:audit:external";

/**
 * US-051 — the built-in system roles (`role_key != null`), keyed in the
 * `ROLE_GRANTS` manifest. Same set as `AppRole`; the alias names the intent
 * (custom roles have `role_key = null` and are NOT in the manifest).
 */
export type SystemRoleKey = AppRole;

/**
 * Role keys that are ALWAYS auditor-group-bound. An auditor holding one of these
 * with an empty `auditorGroupIds` is a broken account.
 *
 * Lives here (US-139) rather than backend-only because `resolveHomePortal` — in
 * `@livingsyncs/permissions`, shared by the frontend guards AND the backend
 * `decide()` — must fail such an account CLOSED instead of letting it fall
 * through into the app portal. The backend's `lib/auditorRoles.ts` re-exports it
 * so its role-assignment guard and this portal decision cannot drift apart.
 */
export const AUDITOR_ROLE_KEYS: readonly AppRole[] = [
  "role:audit:admin",
  "role:audit:external",
];

export interface CurrentShiftInfo {
  shiftId: string;
  facilityId: string;
  facilityName: string;
  /** shifts.shift_type (day | evening | night | on_call) — pre-fills per-shift documentation forms (US-086 UI-2) */
  shiftType: string;
  /** Facility-local calendar date of the shift (YYYY-MM-DD). Lets the client
   * anchor shift-progress correctly for overnight shifts without tz math. */
  shiftDate: string;
  /** IANA timezone of the shift's facility (e.g. "America/Chicago"). The client
   * resolves shift-local dates/times in THIS zone, not the browser's, so
   * display reads identically regardless of the viewer's device timezone. */
  timeZone: string | null;
  /** ISO timestamp of the check-in (shifts.clock_in_time). Drives the header
   * "On duty since …" label + live elapsed counter (SCRUM-162). */
  clockInTime: string | null;
  position: string | null;
  canClockOut: boolean;
}

/**
 * US-176 — machine-readable reason carried as `code` on a 403 from the
 * fast-reauth setup routes (`/api/me/reauth-credential`, `/api/me/pin`) and
 * from withAuth's `requireMfaProvenance` gate. Lets the client tell the user
 * what to actually do instead of one generic "permission denied" message.
 * Only ever describes the CALLER'S OWN account state, so it discloses
 * nothing the caller couldn't already see about themselves.
 */
export type ReauthDenialCode =
  | "MFA_PROVENANCE_REQUIRED"
  | "NO_EMPLOYEE_RECORD"
  | "NO_OPEN_SHIFT"
  // The server could not judge MFA provenance right now (Cognito lookup
  // failed, or the PIN client isn't configured) — nothing the user can fix.
  | "FAST_REAUTH_UNAVAILABLE";

/** US-176 — see `AuthContext.authFlow`. */
export type AuthFlow = "full" | "pin";

export interface AuthContext {
  userId: string;
  email: string;
  /** Full name from employees record — null for users without an employee record */
  name?: string | null;
  /** employees.id — null for users without an employee record (e.g. platform_admin) */
  employeeId: string | null;
  groupHomeIds: string[];
  groupHomeNames: string[];
  facilityIds: string[];
  auditorGroupIds: string[];
  auditorGroupNames: string[];
  roles: AppRole[];
  /** Pre-loaded from role_permissions table — all allowed keys for this user's roles */
  permissions: string[];
  isPlatformAdmin: boolean;
  isPlatformManager: boolean;
  isGroupHomeAdmin: boolean;
  isAuditorGroupAdmin: boolean;
  /**
   * US-176 — how this session was authenticated, read from the ID token's
   * `aud` (the Cognito app client that issued it; Cognito-enforced, and it
   * survives refresh). "full" = the main client, which only allows a password
   * (SRP) sign-in; "pin" = the dedicated re-auth client, which only allows the
   * PIN + device-signature challenge.
   *
   * There is deliberately no `mfaVerified` flag here: user-pool ID tokens carry
   * no `cognito:amr` claim, so nothing in a token says whether MFA was used.
   * The backend answers that on demand (`isMfaVerifiedSession`).
   */
  authFlow: AuthFlow;
  /**
   * US-176 Phase 2 — whether this employee already has a fast-reauth PIN set
   * (`employee_pins` has a row for them). Populated ONLY by `GET /auth/me`
   * (backend/src/app/api/auth/me/route.ts) — every other of ~360 authed
   * routes builds `AuthContext` via `getAuthContext()` without this field, so
   * it is `undefined` there, not `false`. Consumers must not read it as a
   * reliable "no PIN" signal outside that one response.
   */
  hasReauthPin?: boolean;
  /**
   * US-013 AC16 — which client surface produced THIS request, derived from
   * the User-Agent at context-construction time. Request-scoped rather than
   * user-scoped: the same person moves between the web app and the desktop
   * shell, and an audit row should say which one they used.
   *
   * Optional because a context can be built in contexts without a request
   * (tests, scheduled tasks); `parseClientSignal` returns `unknown` rather
   * than guessing when the UA is absent.
   */
  client?: ClientSignal;
  /** Role assumption */
  actualRoles?: AppRole[];
  assumedRole?: AppRole;
  isAssuming: boolean;
  /** Facility context */
  currentShift: CurrentShiftInfo | null;
  isOnShift: boolean;
  defaultFacilityId: string | null;
  facilityContextSource: "shift" | "grant" | "default";
  /**
   * US-179 — the facility the CLIENT says the user is currently working in,
   * carried on every request as `X-Facility-Context`.
   *
   * ⚠️ AUDIT-ONLY. This value is supplied by the client and must NEVER widen
   * access: it does not appear in `facilityIds`, in `decide()`, or in any
   * permission or scoping decision. It exists so a facility switch can be
   * recorded in the audit trail without a separate fire-and-forget request
   * that fails silently (F-013). Authorization keeps reading `facilityIds`.
   *
   * `"all"` is the explicit "All facilities" selection, which is a different
   * event from "nothing selected" (`null`) and must stay distinguishable.
   *
   * OPTIONAL by design: during a rollout an older bundle sends no header, and
   * non-browser callers never will, so "absent" is a real state distinct from
   * "explicitly all facilities".
   */
  viewedFacilityContext?: string | null;
  /**
   * US-143 — legal execution gate. `blocked` means the organization has no
   * executed onboarding packet and must not use the app portal.
   *
   * `outstandingResigns` is INFORMATIONAL and never blocks: a material document
   * update notifies and reminds rather than locking a live customer out of care
   * records.
   *
   * Optional so a context assembled without it (an old fixture, a partial mock)
   * reads as ungated rather than silently blocking every route.
   */
  legalGate?: LegalGateState;
}

/** Credentials used by the frontend login form */
export interface LoginCredentials {
  email: string;
  password: string;
}

/** Data used by the frontend signup form */
export interface SignupData {
  email: string;
  password: string;
  fullName?: string;
}
